import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingBagIcon, CheckIcon, ClockIcon, FlameIcon } from 'lucide-react';
type SocialProofItem = {
  name: string;
  product: string;
  time: string;
  volume: string;
};
const ITEMS: SocialProofItem[] = [{
  name: 'Carlos Fernández',
  product: 'Protocolo Energía Real',
  time: 'hace 8 minutos',
  volume: '1,2k este mes'
}, {
  name: 'Malena Ríos',
  product: 'Protocolo Energía Real',
  time: 'hace 3 minutos',
  volume: '1,2k este mes'
}, {
  name: 'Sebastián Ortiz',
  product: 'Protocolo Energía Real',
  time: 'hace 12 minutos',
  volume: '1,2k este mes'
}, {
  name: 'Julieta Ávila',
  product: 'Protocolo Energía Real',
  time: 'hace 1 minuto',
  volume: '1,2k este mes'
}, {
  name: 'Federico Blanco',
  product: 'Protocolo Energía Real',
  time: 'hace 21 minutos',
  volume: '1,2k este mes'
}];
const FIRST_DELAY_MS = 3500;
const VISIBLE_MS = 4800;
const HIDDEN_MS = 6500;
type SocialProofToastProps = {
  className?: string;
};

/**
 * Floating social-proof toast. Rendered through a portal on document.body so
 * no ancestor with transform/overflow can trap the fixed positioning, keeping
 * it anchored to the bottom of the viewport at any scroll position.
 */
export function SocialProofToast({
  className = ''
}: SocialProofToastProps) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [liftedAboveCta, setLiftedAboveCta] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);
  useEffect(() => setMounted(true), []);

  // En mobile la barra de compra vive abajo: levantamos el toast solo cuando aparece.
  useEffect(() => {
    const update = () => {
      const isMobile = window.matchMedia('(max-width: 639px)').matches;
      setLiftedAboveCta(isMobile && window.scrollY > 900);
    };
    update();
    window.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  useEffect(() => {
    const schedule = (delay: number, action: () => void) => {
      timeoutRef.current = window.setTimeout(action, delay);
    };
    const show = () => {
      setVisible(true);
      schedule(VISIBLE_MS, hide);
    };
    const hide = () => {
      setVisible(false);
      schedule(HIDDEN_MS, () => {
        setIndex(current => (current + 1) % ITEMS.length);
        show();
      });
    };
    schedule(FIRST_DELAY_MS, show);
    return () => window.clearTimeout(timeoutRef.current);
  }, []);
  if (!mounted) return null;
  const item = ITEMS[index];
  return createPortal(<div style={{
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: liftedAboveCta ? 84 : 12,
    margin: '0 auto',
    maxWidth: 302,
    zIndex: 45,
    pointerEvents: 'none',
    transition: 'bottom 220ms ease'
  }} className={className} aria-live="polite" aria-atomic="true" data-id="element-217" data-mp="2gfp0b8e72t0g">
      <AnimatePresence mode="wait" data-id="element-218" data-mp="26aywewz8detl">
        {visible ? <motion.article key={index} initial={{
        opacity: 0,
        y: 28,
        scale: 0.94
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 28,
        scale: 0.97,
        transition: {
          duration: 0.28,
          ease: 'easeIn'
        }
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 18,
        mass: 0.7
      }} className="pointer-events-auto flex items-center gap-2.5 rounded-2xl border border-[#EDEFEE] bg-white px-3 py-2.5 shadow-[0_6px_18px_-8px_rgba(16,24,20,0.2)]" data-id="element-219" data-mp="1sl58cgvjuykx">
            {/* Ícono de compra + verificación */}
            <div className="relative shrink-0" data-id="element-220" data-mp="33zsbae9t80rv">
              <motion.span aria-hidden="true" initial={{
            opacity: 0.5,
            scale: 1
          }} animate={{
            opacity: 0,
            scale: 1.5
          }} transition={{
            duration: 0.8,
            delay: 0.3,
            ease: 'easeOut'
          }} className="absolute inset-0 rounded-[10px] ring-2 ring-[#16A34A]" data-id="element-221" data-mp="34022u6ai6fzo" />
              <span className="relative flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#E8F7EE]" data-id="element-222" data-mp="yxwkk77u7zvd">
                <ShoppingBagIcon className="h-[15px] w-[15px] text-[#16A34A]" strokeWidth={1.5} aria-hidden="true" data-id="element-223" data-mp="ux4bpf2vnwzm" />
              </span>
              <motion.span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[#16A34A]" initial={{
            scale: 0.4,
            boxShadow: '0 0 0 0 rgba(22,163,74,0.55)'
          }} animate={{
            scale: [0.4, 1.15, 1],
            boxShadow: ['0 0 0 0 rgba(22,163,74,0.55)', '0 0 0 7px rgba(22,163,74,0)', '0 0 0 0 rgba(22,163,74,0)']
          }} transition={{
            duration: 0.9,
            delay: 0.18,
            ease: 'easeOut'
          }} data-id="element-224" data-mp="l6hzzvya7qm7">
                <CheckIcon className="h-2.5 w-2.5 text-white" strokeWidth={3.5} aria-hidden="true" data-id="element-225" data-mp="2isiw6tvu792k" />
              </motion.span>
            </div>

            {/* Texto */}
            <div className="min-w-0 flex-1" data-id="element-226" data-mp="2rtz6l1mnjb6s">
              <p className="truncate text-[13px] leading-snug text-[#111827]" data-id="element-227" data-mp="2rso18xdk5vdk">
                <span className="font-bold" data-id="element-228" data-mp="1v6m3n19u0i3s">{item.name}</span> compró
              </p>
              <p className="truncate text-[13px] font-bold leading-snug text-[#111827]" data-id="element-229" data-mp="1z6w9mdxmj8in">
                {item.product}
              </p>

              <div className="mt-1 flex items-center gap-1.5" data-id="element-230" data-mp="28w61ptsgwr6r">
                <span className="flex shrink-0 items-center gap-1 text-[11px] text-[#9AA3A0]" data-id="element-231" data-mp="28xqw9jes8a4m">
                  <ClockIcon className="h-2.5 w-2.5" strokeWidth={1.75} aria-hidden="true" data-id="element-232" data-mp="1bg8yzkwbq4se" />
                  {item.time}
                </span>
                <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#FF9500] px-1.5 py-[3px] text-[10.5px] font-extrabold leading-none text-white" data-id="element-233" data-mp="2jqiyo98nplsu">
                  <FlameIcon className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden="true" data-id="element-234" data-mp="2erft8norldkc" />
                  {item.volume}
                </span>
              </div>
            </div>
          </motion.article> : null}
      </AnimatePresence>
    </div>, document.body);
}